import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faPlus, faTrash, faEdit, faFileCsv,
  faCheckCircle, faExclamationTriangle, faSyncAlt,
  faUser, faEnvelope, faPhone, faMapMarkerAlt, faSpinner
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import Dexie from 'dexie';
import { Link } from 'react-router-dom';
import { ProfilClientContext } from '../layout/CunstomProfilClient';

// ---- Cache Dexie (persistant) ----
const db = new Dexie('ClientsCacheDB');
db.version(1).stores({
  clients: 'key, data, timestamp'
});
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCachedClients(key) {
  try {
    const entry = await db.clients.get(key);
    if (entry && Date.now() - entry.timestamp < CACHE_DURATION) {
      return entry.data;
    }
    return null;
  } catch { return null; }
}

async function setCachedClients(key, data) {
  try {
    await db.clients.put({ key, data, timestamp: Date.now() });
  } catch { /* ignore */ }
}

async function getStaleCache(key) {
  try {
    const entry = await db.clients.get(key);
    return entry ? entry.data : null;
  } catch { return null; }
}

const API_URL = `${API_CONFIG2.apiUrl}client/api_client.php`;

// ---- Dédoublonnage des requêtes en cours ----
const pendingFetches = new Map();

// ---- UTILITAIRE FETCH JSON ----
const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, options);
  const text = await response.text();
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} : ${text || 'Erreur inconnue'}`);
  }
  try {
    return JSON.parse(text);
  } catch (parseError) {
    throw new Error(`Réponse JSON invalide : ${text.substring(0, 200) || '(réponse vide)'}`);
  }
};

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================
const ManageClients = ({ userId, idcl }) => {
  const context = useContext(ProfilClientContext) || {};
  const { refreshKey, setSource } = context;

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRecords: 0, limit: 10 });

  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({ idcl: '', nom: '', email: '', telephone: '', adresse: '' });
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [selectedFile, setSelectedFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [dataSource, setDataSource] = useState('cache');

  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isMounted = useRef(true);

  // ---- Fonction de chargement avec dédoublonnage et annulation ----
  const fetchClients = useCallback(async (page = 1, search = '', forceRefresh = false, retryCount = 0) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const cacheKey = `page_${page}_search_${search}`;
    const requestKey = forceRefresh ? `${cacheKey}_force` : cacheKey;

    // Dédoublonnage
    if (pendingFetches.has(requestKey) && !forceRefresh) {
      try {
        const result = await pendingFetches.get(requestKey);
        if (isMounted.current && result) {
          setClients(result.data);
          setPagination(result.pagination);
          setDataSource('cache (partagé)');
          if (setSource) setSource('cache (partagé)');
        }
        return;
      } catch {
        pendingFetches.delete(requestKey);
      }
    }

    // Limiter les tentatives pour éviter les boucles infinies
    if (retryCount > 5) {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
        setToast({ show: true, message: 'Erreur : trop de tentatives. Veuillez réessayer plus tard.', variant: 'danger' });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
      }
      return;
    }

    // Définir l'état de chargement
    if (forceRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    const fetchPromise = (async () => {
      try {
        // 1. Cache (sauf forceRefresh)
        if (!forceRefresh) {
          const cached = await getCachedClients(cacheKey);
          if (cached && isMounted.current) {
            setClients(cached.data);
            setPagination(cached.pagination);
            setDataSource('cache');
            if (setSource) setSource('cache');
            setLoading(false);
            setRefreshing(false);
            return cached;
          }
        }

        // 2. Requête réseau
        const url = `${API_URL}?action=fetch&page=${page}&limit=${pagination.limit}&search=${encodeURIComponent(search)}`;
        const response = await fetch(url, { signal: controller.signal });

        // Gestion 429
        if (response.status === 429) {
          const stale = await getStaleCache(cacheKey);
          if (stale && isMounted.current) {
            setClients(stale.data);
            setPagination(stale.pagination);
            setDataSource('cache (périmé)');
            if (setSource) setSource('cache (périmé)');
            setToast({ show: true, message: '429 – Utilisation du cache périmé', variant: 'warning' });
            setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
            setLoading(false);
            setRefreshing(false);
            return stale;
          }
          // Sinon, réessayer avec backoff
          const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return fetchClients(page, search, forceRefresh, retryCount + 1);
        }

        if (!response.ok) {
          const stale = await getStaleCache(cacheKey);
          if (stale && isMounted.current) {
            setClients(stale.data);
            setPagination(stale.pagination);
            setDataSource('cache (périmé)');
            if (setSource) setSource('cache (périmé)');
            setToast({ show: true, message: `Erreur réseau – Utilisation du cache`, variant: 'warning' });
            setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
            setLoading(false);
            setRefreshing(false);
            return stale;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const text = await response.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch (parseError) {
          throw new Error(`Réponse JSON invalide : ${text.substring(0, 200)}`);
        }

        if (data.status === 'success') {
          const result = { data: data.data, pagination: data.pagination };
          if (isMounted.current) {
            setClients(data.data);
            setPagination(data.pagination);
            const source = forceRefresh ? 'live (forcé)' : 'live';
            setDataSource(source);
            if (setSource) setSource(source);
            await setCachedClients(cacheKey, result);
            if (forceRefresh) {
              setToast({ show: true, message: 'Données actualisées', variant: 'success' });
              setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
            }
            setLoading(false);
            setRefreshing(false);
          }
          return result;
        } else {
          throw new Error(data.message || 'Erreur serveur');
        }
      } catch (err) {
        if (err.name === 'AbortError') return null;
        // Dernier recours : cache périmé
        const stale = await getStaleCache(cacheKey);
        if (stale && isMounted.current) {
          setClients(stale.data);
          setPagination(stale.pagination);
          setDataSource('cache (périmé)');
          if (setSource) setSource('cache (périmé)');
          setToast({ show: true, message: `Erreur – Utilisation du cache : ${err.message}`, variant: 'warning' });
          setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
          setLoading(false);
          setRefreshing(false);
          return stale;
        }
        if (isMounted.current) {
          setToast({ show: true, message: `Erreur : ${err.message}`, variant: 'danger' });
          setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
          setLoading(false);
          setRefreshing(false);
        }
        throw err;
      }
    })();

    pendingFetches.set(requestKey, fetchPromise);
    try {
      const result = await fetchPromise;
      return result;
    } finally {
      pendingFetches.delete(requestKey);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [pagination.limit, setSource]);

  // ---- Gestion de la recherche (debounce) ----
  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => fetchClients(1, val, false), 400);
  };

  // ---- Chargement initial et nettoyage ----
  useEffect(() => {
    isMounted.current = true;
    fetchClients();
    return () => {
      isMounted.current = false;
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
      pendingFetches.clear();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Réaction au refreshKey du contexte ----
  useEffect(() => {
    if (refreshKey && refreshKey > 0) {
      fetchClients(pagination.currentPage, searchTerm, true);
    }
  }, [refreshKey, fetchClients, pagination.currentPage, searchTerm]);

  // ---- EXPORT CSV ----
  const exportCSV = () => {
    if (!clients.length) {
      setToast({ show: true, message: 'Aucun client à exporter.', variant: 'warning' });
      setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
      return;
    }
    const headers = ['ID', 'Nom', 'Email', 'Téléphone', 'Adresse'];
    const rows = clients.map(c => [c.idcl, c.nom, c.email, c.telephone, c.adresse]);
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `clients_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setToast({ show: true, message: 'Export CSV réussi', variant: 'success' });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
  };

  // ---- CRUD (avec rechargement forcé) ----
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const method = editingClient ? 'PUT' : 'POST';
    const action = editingClient ? 'update' : 'create';
    const payload = { ...formData };
    if (editingClient) payload.idcl = parseInt(formData.idcl);
    try {
      const data = await fetchJSON(`${API_URL}?action=${action}`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (data.status === 'success') {
        setToast({ show: true, message: data.message, variant: 'success' });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
        setShowModal(false);
        await db.clients.clear(); // invalider tout le cache
        fetchClients(pagination.currentPage, searchTerm, true);
      } else {
        throw new Error(data.message || 'Erreur serveur');
      }
    } catch (err) {
      setToast({ show: true, message: `Erreur : ${err.message}`, variant: 'danger' });
      setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      const data = await fetchJSON(`${API_URL}?action=delete&idcl=${formData.idcl}`, { method: 'DELETE' });
      if (data.status === 'success') {
        setToast({ show: true, message: data.message, variant: 'success' });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
        setShowDeleteModal(false);
        await db.clients.clear();
        fetchClients(pagination.currentPage, searchTerm, true);
      } else {
        throw new Error(data.message || 'Erreur serveur');
      }
    } catch (err) {
      setToast({ show: true, message: `Erreur : ${err.message}`, variant: 'danger' });
      setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleImportCSV = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      setToast({ show: true, message: 'Veuillez sélectionner un fichier CSV', variant: 'danger' });
      setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
      return;
    }
    setSubmitting(true);
    const formDataFile = new FormData();
    formDataFile.append('file', selectedFile);
    try {
      const data = await fetchJSON(`${API_URL}?action=import`, { method: 'POST', body: formDataFile });
      if (data.status === 'success') {
        setToast({ show: true, message: data.message, variant: 'success' });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
        setShowImportModal(false);
        setSelectedFile(null);
        await db.clients.clear();
        fetchClients(1, searchTerm, true);
      } else {
        throw new Error(data.message || 'Erreur serveur');
      }
    } catch (err) {
      setToast({ show: true, message: `Erreur : ${err.message}`, variant: 'danger' });
      setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (client) => {
    setEditingClient(client);
    setFormData({ ...client });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingClient(null);
    setFormData({ idcl: '', nom: '', email: '', telephone: '', adresse: '' });
    setShowModal(true);
  };

  // ---- RENDU ----
  return (
    <>
      <style>{`
        /* ============================================================
           DARK BLUE GLASSMORPHISM – MANAGE CLIENTS (mobile optimisé)
           ============================================================ */
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-dim: rgba(59, 130, 246, 0.10);
          --accent-blue-glow: rgba(59, 130, 246, 0.25);
          --bg-primary: #0b1428;
          --bg-gradient: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          --card-bg: rgba(20, 40, 80, 0.55);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 16px;
          --shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
        }

        .monetra-page {
          background: var(--bg-gradient);
          min-height: 100vh;
          padding: clamp(0.75rem, 2vw, 2rem);
          color: var(--text-primary);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: 24px;
          padding: clamp(1rem, 1.5vw, 1.8rem);
          box-shadow: var(--shadow);
          transition: all 0.2s ease;
          margin-bottom: 24px;
        }
        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.25);
          box-shadow: 0 12px 40px rgba(59, 130, 246, 0.05);
        }

        .monetra-btn {
          padding: 8px 16px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 500;
          cursor: pointer;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
          white-space: nowrap;
          min-height: 44px;
          touch-action: manipulation;
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.2);
        }
        .monetra-btn:disabled {
          opacity: 0.5;
          pointer-events: none;
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px var(--accent-blue-glow);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: var(--accent-dark-blue);
          box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4);
        }
        .monetra-btn.danger {
          border-color: #ef4444;
          color: #f87171;
        }
        .monetra-btn.danger:hover:not(:disabled) {
          background: rgba(239, 68, 68, 0.12);
        }
        .monetra-btn.outline-secondary {
          border-color: rgba(255,255,255,0.08);
          color: var(--text-secondary);
        }
        .monetra-btn.outline-secondary:hover:not(:disabled) {
          background: rgba(255,255,255,0.05);
        }
        .monetra-btn.p-2 {
          padding: 6px 10px;
          min-width: 36px;
          justify-content: center;
        }

        .badge-source {
          font-size: clamp(0.6rem, 0.9vw, 0.75rem);
          padding: 4px 12px;
          border-radius: 40px;
          background: rgba(59,130,246,0.15);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.12);
          white-space: nowrap;
          font-weight: 500;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.15);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.15);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.12);
        }

        .search-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          padding: 4px 4px 4px 16px;
          transition: all 0.3s ease;
          flex: 1;
          min-width: 180px;
        }
        .search-wrapper:focus-within {
          border-color: var(--accent-blue);
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.08);
        }
        .search-wrapper input {
          background: transparent;
          border: none;
          color: var(--text-primary);
          outline: none;
          padding: 10px 0;
          flex: 1;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }
        .search-wrapper input::placeholder {
          color: var(--text-muted);
        }

        .glass-table {
          width: 100%;
          border-collapse: collapse;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
        }
        .glass-table th {
          padding: 12px 10px;
          text-align: left;
          font-size: clamp(0.6rem, 0.8vw, 0.75rem);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--accent-blue);
          border-bottom: 1px solid rgba(255,255,255,0.04);
          font-weight: 600;
        }
        .glass-table td {
          padding: 10px 10px;
          border-bottom: 1px solid rgba(255,255,255,0.02);
          vertical-align: middle;
        }
        .glass-table tr {
          transition: 0.2s;
        }
        .glass-table tr:hover {
          background: rgba(255,255,255,0.03);
        }

        .monetra-modal .modal-content {
          background: rgba(10, 20, 45, 0.92);
          backdrop-filter: blur(30px);
          -webkit-backdrop-filter: blur(30px);
          border: 1px solid rgba(59, 130, 246, 0.12);
          border-radius: 28px;
          color: var(--text-primary);
          box-shadow: 0 30px 80px rgba(0,0,0,0.8);
        }
        .monetra-modal .modal-header {
          border-bottom: 1px solid rgba(59, 130, 246, 0.06);
        }
        .monetra-modal .btn-close {
          filter: invert(1) brightness(200%);
          opacity: 0.5;
        }

        .monetra-input {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 14px;
          color: var(--text-primary);
          padding: 12px;
          width: 100%;
          transition: 0.3s;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }
        .monetra-input:focus {
          border-color: var(--accent-blue);
          outline: none;
          background: rgba(255,255,255,0.06);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.06);
        }
        .monetra-input::placeholder {
          color: var(--text-muted);
        }

        .toast-monetra {
          background: rgba(10, 20, 45, 0.8) !important;
          backdrop-filter: blur(24px) !important;
          -webkit-backdrop-filter: blur(24px) !important;
          border: 1px solid rgba(59, 130, 246, 0.2) !important;
          border-radius: 16px !important;
          color: var(--text-primary) !important;
          box-shadow: 0 20px 50px rgba(0,0,0,0.6) !important;
        }

        .text-secondary { color: var(--text-secondary); }
        .text-muted { color: var(--text-muted); }
        .text-success { color: var(--accent-blue); }
        .text-danger { color: #ef4444; }
        .text-info { color: var(--accent-blue); }
        .fw-bold { font-weight: 600; }
        .small { font-size: 0.85rem; }
        .m-0 { margin: 0; }
        .mb-4 { margin-bottom: 1.5rem; }
        .me-2 { margin-right: 0.5rem; }
        .p-2 { padding: 0.5rem; }
        .p-3 { padding: 1rem; }
        .py-5 { padding-top: 3rem; padding-bottom: 3rem; }
        .gap-2 { gap: 0.5rem; }
        .gap-3 { gap: 1rem; }
        .d-flex { display: flex; }
        .flex-wrap { flex-wrap: wrap; }
        .align-items-center { align-items: center; }
        .justify-content-between { justify-content: space-between; }
        .text-end { text-align: right; }
        .text-center { text-align: center; }
        .overflow-hidden { overflow: hidden; }
        .table-responsive { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .border-top { border-top: 1px solid rgba(255,255,255,0.04); }
        .border-opacity-10 { opacity: 0.1; }
        .text-decoration-none { text-decoration: none; }

        /* ============================================================
           RESPONSIVE MOBILE
           ============================================================ */
        @media (max-width: 768px) {
          .monetra-page { padding: 12px; }
          .glass-card { padding: 1rem; border-radius: 16px; }

          .d-flex.justify-content-between.align-items-center {
            flex-direction: column;
            align-items: stretch !important;
            gap: 12px;
          }
          .d-flex.justify-content-between.align-items-center > div:first-child {
            text-align: center;
          }
          .d-flex.gap-2 {
            flex-wrap: wrap;
            justify-content: center;
          }
          .monetra-btn {
            font-size: 0.75rem;
            padding: 6px 12px;
            min-height: 38px;
            flex: 1 0 auto;
            justify-content: center;
          }
          .monetra-btn.p-2 {
            padding: 4px 8px;
            min-width: 32px;
          }

          .search-wrapper {
            max-width: 100%;
            min-width: unset;
          }
          .glass-table th, .glass-table td {
            padding: 6px 8px;
            font-size: 0.7rem;
          }
          .glass-table th:last-child, .glass-table td:last-child {
            min-width: 80px;
          }

          .monetra-modal .modal-dialog {
            margin: 0.5rem;
          }
          .monetra-modal .modal-content {
            border-radius: 20px;
          }
          .monetra-modal .modal-footer {
            flex-direction: column-reverse;
          }
          .monetra-modal .modal-footer button {
            width: 100%;
            justify-content: center;
          }
        }

        @media (max-width: 480px) {
          .monetra-btn {
            font-size: 0.7rem;
            padding: 4px 10px;
            min-height: 34px;
          }
          .glass-table th, .glass-table td {
            font-size: 0.6rem;
            padding: 4px 6px;
          }
        }
      `}</style>

      <div className="monetra-page">
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
          <div>
            <h1 className="fw-bold mb-1" style={{ fontSize: 'clamp(1.4rem, 4vw, 2.2rem)' }}>
              Gestion des clients
            </h1>
            <p className="text-secondary m-0" style={{ fontSize: 'clamp(0.75rem, 1.2vw, 0.95rem)' }}>
              Gérez vos clients, importez et mettez à jour leurs informations.
            </p>
          </div>
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <span className={`badge-source ${dataSource === 'live' ? 'live' : dataSource === 'cache (périmé)' ? 'warning' : ''}`}>
              {dataSource === 'cache' ? '📦 Cache' :
               dataSource === 'cache (périmé)' ? '⚠️ Cache périmé' :
               dataSource === 'cache (partagé)' ? '🔄 Partage' :
               dataSource === 'live' ? '🔄 Live' :
               dataSource === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
            </span>
            <button className="monetra-btn" onClick={() => fetchClients(pagination.currentPage, searchTerm, false)} disabled={loading || refreshing}>
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} /> Actualiser
            </button>
            <button className="monetra-btn" onClick={() => fetchClients(pagination.currentPage, searchTerm, true)} disabled={loading || refreshing}>
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} style={{ color: '#ef4444' }} /> Forcer
            </button>
            <button className="monetra-btn" onClick={exportCSV} disabled={loading || refreshing || clients.length === 0}>
              <FontAwesomeIcon icon={faFileCsv} /> Exporter CSV
            </button>
            <button className="monetra-btn" onClick={() => setShowImportModal(true)} disabled={loading || refreshing}>
              <FontAwesomeIcon icon={faFileCsv} /> Importer CSV
            </button>
            <button className="monetra-btn primary" onClick={openCreateModal} disabled={loading || refreshing}>
              <FontAwesomeIcon icon={faPlus} /> Ajouter
            </button>
          </div>
        </div>

        <div className="glass-card mb-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div className="search-wrapper" style={{ minWidth: '260px', flex: 1, maxWidth: '400px' }}>
            <FontAwesomeIcon icon={faSearch} className="text-secondary" />
            <input type="text" placeholder="Rechercher (nom, email, téléphone...)" value={searchTerm} onChange={handleSearch} />
          </div>
          <div className="text-secondary small">
            <span>{pagination.totalRecords} enregistrements</span>
          </div>
        </div>

        <div className="glass-card p-0 overflow-hidden">
          <div className="table-responsive">
            <table className="glass-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nom</th>
                  <th>Email</th>
                  <th>Téléphone</th>
                  <th>Adresse</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && !refreshing ? (
                  <tr><td colSpan="6" className="text-center py-5"><div className="spinner-border text-primary" role="status"></div></td></tr>
                ) : clients.length === 0 ? (
                  <tr><td colSpan="6" className="text-center py-5 text-secondary">Aucun client trouvé.</td></tr>
                ) : (
                  clients.map(client => (
                    <tr key={client.idcl}>
                      <td><span className="fw-bold text-secondary">#{client.idcl}</span></td>
                      <td>
                        <Link to={`/client-Profil/${client.idcl}`} className="text-decoration-none text-success">
                          {client.nom}
                        </Link>
                      </td>
                      <td><span className="text-secondary">{client.email}</span></td>
                      <td><span className="text-secondary">{client.telephone}</span></td>
                      <td><span className="text-secondary">{client.adresse}</span></td>
                      <td className="text-end">
                        <button className="monetra-btn p-2 me-1" onClick={() => openEditModal(client)} title="Modifier">
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button className="monetra-btn danger p-2" onClick={() => { setFormData(client); setShowDeleteModal(true); }} title="Supprimer">
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="p-3 border-top border-secondary border-opacity-10 d-flex justify-content-between align-items-center flex-wrap">
            <span className="text-secondary small">Page {pagination.currentPage} sur {pagination.totalPages}</span>
            <div className="d-flex gap-2">
              <button className="monetra-btn p-2" disabled={pagination.currentPage <= 1 || loading || refreshing} onClick={() => fetchClients(pagination.currentPage - 1, searchTerm, false)}>‹</button>
              <button className="monetra-btn p-2" disabled={pagination.currentPage >= pagination.totalPages || loading || refreshing} onClick={() => fetchClients(pagination.currentPage + 1, searchTerm, false)}>›</button>
            </div>
          </div>
        </div>
      </div>

      {/* MODALES (inchangées) */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered className="monetra-modal">
        <Form onSubmit={handleFormSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon={faEdit} className="text-success me-2" />
              {editingClient ? 'Modifier' : 'Ajouter'} un client
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="text-secondary small">Nom</label>
                <input className="monetra-input" name="nom" value={formData.nom} onChange={(e) => setFormData({...formData, nom: e.target.value})} required disabled={submitting} />
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Email</label>
                <input type="email" className="monetra-input" name="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required disabled={submitting} />
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Téléphone</label>
                <input className="monetra-input" name="telephone" value={formData.telephone} onChange={(e) => setFormData({...formData, telephone: e.target.value})} required disabled={submitting} />
              </div>
              <div className="col-12">
                <label className="text-secondary small">Adresse</label>
                <textarea className="monetra-input" name="adresse" value={formData.adresse} onChange={(e) => setFormData({...formData, adresse: e.target.value})} required rows="2" disabled={submitting} />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowModal(false)} className="monetra-btn outline-secondary" disabled={submitting}>Annuler</Button>
            <Button variant="success" type="submit" className="monetra-btn primary" disabled={submitting}>
              {submitting ? (
                <><FontAwesomeIcon icon={faSpinner} spin className="me-2" /> En cours...</>
              ) : (
                'Enregistrer'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="monetra-modal">
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            Confirmation
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Êtes-vous sûr de vouloir supprimer le client <strong>{formData.nom}</strong> ?
          <br /><span className="text-secondary small">Cette action est irréversible.</span>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)} className="monetra-btn outline-secondary" disabled={submitting}>Annuler</Button>
          <Button variant="danger" onClick={handleDelete} className="monetra-btn danger" disabled={submitting}>
            {submitting ? (
              <><FontAwesomeIcon icon={faSpinner} spin className="me-2" /> Suppression...</>
            ) : (
              'Confirmer la suppression'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showImportModal} onHide={() => setShowImportModal(false)} centered className="monetra-modal">
        <Form onSubmit={handleImportCSV}>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon={faFileCsv} className="text-info me-2" />
              Importer des données CSV
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-secondary small">Le fichier doit avoir les colonnes dans cet ordre :<br /> 
              <code>nom, email, telephone, adresse</code>
            </p>
            <input type="file" accept=".csv" className="monetra-input" onChange={(e) => setSelectedFile(e.target.files[0])} required disabled={submitting} />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowImportModal(false)} className="monetra-btn outline-secondary" disabled={submitting}>Annuler</Button>
            <Button variant="success" type="submit" className="monetra-btn primary" disabled={submitting}>
              {submitting ? (
                <><FontAwesomeIcon icon={faSpinner} spin className="me-2" /> Importation...</>
              ) : (
                'Importer'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* TOAST */}
      {toast.show && (
        <div style={{ position: 'fixed', top: '24px', right: '24px', zIndex: 9999, maxWidth: '440px', width: '100%' }}>
          <div className={`toast-monetra p-3 ${toast.variant}`}>
            <div className="d-flex align-items-center gap-3">
              <FontAwesomeIcon
                icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
                className={toast.variant === 'success' ? 'text-success' : 'text-danger'}
              />
              <span className="flex-grow-1" style={{ fontSize: 'clamp(0.85rem, 1.2vw, 1rem)' }}>{toast.message}</span>
              <button
                className="btn-close-custom"
                onClick={() => setToast({ ...toast, show: false })}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-primary)', opacity: 0.6, fontSize: '1.2rem', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ManageClients;